import {
  alpha,
  Box,
  Chip,
  MenuItem,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
} from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { DataGridPro } from '../../../tables/DataGridPro';
import capitalize from 'lodash/capitalize';
import lowerFirst from 'lodash/lowerFirst';
import upperCase from 'lodash/upperCase';
import TextSearch from '../../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import * as monitoringSlice from '../../../../data/monitoring.slice';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import {
  DateRange,
  SendMessageType,
} from '../../../../data/models/simplified-workflow.model';
import { phoneUtils } from '../../../../services/phoneUtils';

interface SimplifiedWorkflowMonitoringProps {
  workflowUUID: string;
  messageType?: SendMessageType;
}

interface NotifLogGridItem {
  id: string;
  sent: string;
  recipient: {
    name: string;
    contact: string;
  };
  status: {
    name: 'success' | 'fail';
    errorMessage?: string;
  };
}

const PREFIX = 'SimplifiedWorkflowMonitoring';

const classes = {
  container: `${PREFIX}-container`,
  search: `${PREFIX}-search`,
  dateRangeSelect: `${PREFIX}-dateRangeSelect`,
  statsContainer: `${PREFIX}-statsContainer`,
  chip: `${PREFIX}-chip`,
  successChip: `${PREFIX}-successChip`,
  failChip: `${PREFIX}-failChip`,
  grid: `${PREFIX}-grid`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  [`& .${classes.search}`]: {
    width: 300,
    marginRight: 20,
  },
  [`& .${classes.dateRangeSelect}`]: {
    width: 180,
  },
  [`& .${classes.statsContainer}`]: {
    marginTop: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.successChip}`]: {
    backgroundColor: alpha(theme.palette.success.main, 0.2),
    '& .MuiChip-label': {
      color: `${theme.palette.success.main} !important`,
    },
  },
  [`& .${classes.failChip}`]: {
    backgroundColor: alpha(theme.palette.error.main, 0.2),
    '& .MuiChip-label': {
      color: `${theme.palette.error.main} !important`,
    },
  },
  [`& .${classes.grid}`]: {
    width: '100%',
  },
}));

export default function SimplifiedWorkflowMonitoring({
  workflowUUID,
  messageType,
}: SimplifiedWorkflowMonitoringProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedDateRange, setSelectedDateRange] = useState(DateRange.Today);
  const smsLogs = useAppSelector(monitoringSlice.selectSMSNotificationLogs);
  const emailLogs = useAppSelector(monitoringSlice.selectEmailNotificationLogs);
  const notificationLogs =
    messageType === SendMessageType.SMS ? smsLogs : emailLogs;

  const gridItems: NotifLogGridItem[] = useMemo(() => {
    if (messageType === SendMessageType.SMS) {
      return smsLogs.messages.map((log) => ({
        id: log.smsMessageTrackingID,
        sent: DateTime.fromISO(log.createdOnUTC).toFormat('M/d/yyyy h:mm a'),
        recipient: {
          name: `${log.TripLeg?.Reservation.Passenger.passengerFirstName ?? ''} ${log.TripLeg?.Reservation.Passenger.passengerLastName ?? ''}`,
          contact: phoneUtils.formatPhoneString(log.phoneNumber),
        },
        status: {
          name: log.isSmsSent ? 'success' : 'fail',
          errorMessage: log.smsStatus,
        },
      }));
    } else if (messageType === SendMessageType.Email) {
      return emailLogs.messages.map((log) => ({
        id: log.emailMessageTrackingID.toString(),
        sent: DateTime.fromISO(log.createdOnUTC).toFormat('M/d/yyyy h:mm a'),
        recipient: {
          name: log.recipientName,
          contact: log.recipientEmail,
        },
        status: {
          name: log.isEmailSent ? 'success' : 'fail',
          errorMessage: log.emailStatus,
        },
      }));
    }
    return [];
  }, [messageType, smsLogs.messages, emailLogs.messages]);

  useEffect(() => {
    if (!workflowUUID) {
      return;
    }
    const dateRange = getDateRangeAsISOString(selectedDateRange);

    if (messageType === SendMessageType.SMS) {
      const phoneWithoutSpecialChars = searchText.replace(/[\s()-]/g, '');
      dispatch(
        monitoringSlice.fetchSMSNotificationLogsAsync(
          workflowUUID,
          dateRange.startDate,
          dateRange.endDate,
          page,
          pageSize,
          phoneWithoutSpecialChars,
        ),
      );
    } else {
      dispatch(
        monitoringSlice.fetchEmailNotificationLogsAsync(
          workflowUUID,
          dateRange.startDate,
          dateRange.endDate,
          page,
          pageSize,
          searchText,
        ),
      );
    }
  }, [
    page,
    pageSize,
    searchText,
    selectedDateRange,
    workflowUUID,
    messageType,
  ]);

  const renderStatus = (params: GridCellParams): React.ReactElement => {
    const status = params.row.status;
    const className = `${status.name}Chip` as 'successChip' | 'failChip';
    const label =
      status.name === 'success'
        ? capitalize(status.name)
        : `${capitalize(status.name)} - ${status.errorMessage}`;
    return <Chip className={classes[className]} label={label} />;
  };

  const columns: GridColDef[] = [
    {
      field: 'sent',
      headerName: t('simplenotifications.sent'),
      sortable: false,
      minWidth: 200,
    },
    {
      field: 'recipient',
      headerName: t('simplenotifications.recipient'),
      sortable: false,
      minWidth: 300,
      valueFormatter: (params) => {
        return `${params.value.name} ${params.value.contact}`;
      },
    },
    {
      field: 'status',
      headerName: t('simplenotifications.status'),
      sortable: false,
      renderCell: renderStatus,
      minWidth: 300,
    },
  ];

  const isLoading = false;
  const stats: MonitoringStatProps[] = [
    {
      label: t('simplenotifications.executions'),
      value: '645',
    },
    {
      label: t('simplenotifications.success'),
      value: '87%',
    },
    {
      label: t('simplenotifications.fail'),
      value: '42',
    },
  ];

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleDateRangeChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange);
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== notificationLogs.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  const dateRanges = Object.values(DateRange).map((dateRange) => ({
    key: dateRange,
    value: t(`simplenotifications.${lowerFirst(dateRange)}`),
  }));

  if (!messageType) {
    return <></>;
  }

  return (
    <StyledBox>
      <Box className={classes.container}>
        <Stack direction="row">
          <TextSearch
            id="text-search"
            className={classes.search}
            icon={<SearchIcon />}
            label={t('workflows.search')}
            placeholder={t('simplenotifications.monitoringSearchPlaceholder')}
            currentSearchFilter={searchText}
            onSearchChange={handleSearchTextChange}
          />
          <TextField
            select
            className={classes.dateRangeSelect}
            label={t('simplenotifications.executed')}
            variant="outlined"
            required={true}
            value={selectedDateRange}
            onChange={(evt) =>
              handleDateRangeChange(evt.target.value as DateRange)
            }>
            {dateRanges?.map((dateRange) => {
              return (
                <MenuItem value={dateRange.key} key={dateRange.key}>
                  {dateRange.value}
                </MenuItem>
              );
            })}
          </TextField>
        </Stack>
        {/*
        Uncomment when we have API for monitoring stats
        <Stack direction="row" className={classes.statsContainer}>
          {stats.map((stat) => (
            <MonitoringStat label={stat.label} value={stat.value} />
          ))}
        </Stack>
        */}
        <DataGridPro
          className={classes.grid}
          disableColumnMenu={true}
          getRowId={(row) => row.id}
          rows={gridItems}
          columns={columns}
          rowCount={notificationLogs.totalItems}
          loading={isLoading}
          pagination={true}
          paginationMode="server"
          paginationModel={{
            page: notificationLogs.currentPage - 1,
            pageSize,
          }}
          pageSizeOptions={[10, 20, 30, 40, 50, 100]}
          onPaginationModelChange={paginationChange}
          autoHeight
        />
      </Box>
    </StyledBox>
  );
}

function getDateRangeAsISOString(range: DateRange): {
  startDate: string;
  endDate: string;
} {
  const now = DateTime.now().startOf('day');
  let startDate: DateTime;
  let endDate: DateTime;

  switch (range) {
    case DateRange.Today:
      startDate = now;
      endDate = now.plus({ days: 1 }).startOf('day');
      break;
    case DateRange.Yesterday:
      startDate = now.minus({ days: 1 });
      endDate = now.startOf('day');
      break;
    case DateRange.ThisWeek:
      startDate = now.startOf('week');
      endDate = now.endOf('week').plus({ days: 1 }).startOf('day');
      break;
    case DateRange.ThisMonth:
      startDate = now.startOf('month');
      endDate = now.endOf('month').plus({ days: 1 }).startOf('day');
      break;
  }

  return {
    startDate: startDate.toISO()!,
    endDate: endDate.toISO()!,
  };
}

interface MonitoringStatProps {
  label: string;
  value: string;
}

function MonitoringStat({ label, value }: MonitoringStatProps) {
  return (
    <Stack direction="column">
      <Typography variant="body2">{upperCase(label)}</Typography>
      <Typography variant="h1">{value}</Typography>
    </Stack>
  );
}
